import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Globe, MapPin, Loader2 } from "lucide-react";
import { useQuery } from "@apollo/client";
import { GET_MARKETPLACE_COUNTRIES } from "@/Graphql/Queries";
import { useToast } from "@/hooks/use-toast";

// Country flag code mapping based on country names
const getCountryFlagCode = (countryName: string): string => {
  const flagMapping: { [key: string]: string } = {
    "Thailand": "th",
    "Malaysia": "my",
    "India": "in",
    "Singapore": "sg",
    "Qatar": "qa",
    "United Arab Emirates": "ae",
    "South Africa": "za",
    "Vietnam": "vn",
    "Indonesia": "id",
    "Philippines": "ph",
    "United States": "us",
    "United Kingdom": "gb",
    "Germany": "de",
    "France": "fr",
    "Japan": "jp",
    "China": "cn",
    "Australia": "au",
    "Canada": "ca",
    "Brazil": "br",
    "Mexico": "mx",
    "South Korea": "kr",
    "Netherlands": "nl",
    "Italy": "it",
    "Spain": "es",
    "Turkey": "tr",
    "Russia": "ru",
    "Saudi Arabia": "sa",
    "Egypt": "eg",
    "Nigeria": "ng",
    "Kenya": "ke",
    "Morocco": "ma",
    "Ghana": "gh",
    // Add more mappings as needed
  };

  return flagMapping[countryName] || countryName.toLowerCase().substring(0, 2);
};

interface CountryData {
  countryId: string;
  name: string;
  iso: string;
  dialingCode: string;
  id: string;
  currencyCode: string;
  region: string;
  tax?: {
    label: string;
    percent: number;
  };
}

interface CountrySelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCountrySelect: (country: CountryData) => void;
  detectedCountry?: string;
  userIP?: string;
  hasInventory?: boolean;
  allowClose?: boolean;
  exposure?: string;
}

export default function CountrySelectionModal({
  open,
  onOpenChange,
  onCountrySelect,
  detectedCountry,
  userIP,
  hasInventory = false,
  allowClose = true,
  exposure = "MARKETPLACE",
}: CountrySelectionModalProps) {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const { toast } = useToast();

  // Fetch country list from API
  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(GET_MARKETPLACE_COUNTRIES, {
    variables: { exposure: exposure },
    onError: (error) => {
      console.error('Country list API error:', error);
      toast({
        title: "Error loading countries",
        description: "Failed to load available countries. Please try again.",
        variant: "destructive"
      });
    }
  });

  const availableCountries = countryData?.getMarketplaceCountries || [];

  useEffect(() => {
    if (availableCountries.length > 0 && !selectedCountry) {
      if (detectedCountry && !hasInventory) {
        // Try to find a matching country but don't auto-select since it's not available
        const searchCountry = detectedCountry.toLowerCase().trim();
        const matchedCountry = availableCountries.find((c: CountryData) =>
          c.name?.toLowerCase().trim() === searchCountry
        );

        if (!matchedCountry) {
          // Auto-select first available country as default suggestion
          setSelectedCountry(availableCountries[0]?.id || "");
        }
      } else {
        // Auto-select first country as default
        setSelectedCountry(availableCountries[0]?.id || "");
      }
    }
  }, [detectedCountry, hasInventory, availableCountries]);

  const handleSelectCountry = () => {
    if (selectedCountry && availableCountries.length > 0) {
      const country = availableCountries.find((c: CountryData) => c.id === selectedCountry);
      if (country) {
        onCountrySelect(country);
        onOpenChange(false);

        toast({
          title: "Country selected",
          description: `You've selected ${country.name}. Showing inventories for this region.`,
        });
      }
    }
  };

  const detectedCountryName = detectedCountry;

  if (countryError) {
    return (
      <Dialog open={open} onOpenChange={undefined}>
        <DialogContent
          className="w-[95vw] max-w-[95vw] sm:max-w-[500px] max-h-[90vh] p-4 sm:p-6 flex flex-col [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-2 sm:space-y-3 pb-2 flex-shrink-0">
            <DialogTitle className="text-center text-xl sm:text-2xl px-2">
              Error Loading Countries
            </DialogTitle>
            <DialogDescription className="text-center px-2">
              Unable to load available countries. Please refresh the page and try again.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 pt-4 flex-shrink-0">
            <Button
              onClick={() => window.location.reload()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-base h-11 font-medium"
            >
              Refresh Page
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={undefined}>
      <DialogContent
        className="w-[95vw] max-w-[95vw] sm:max-w-[500px] max-h-[90vh] p-4 sm:p-6 flex flex-col [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >

        <DialogHeader className="space-y-2 sm:space-y-3 flex-shrink-0">
          <div className="flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-emerald-50 dark:bg-emerald-900/20 rounded-full">
            <Globe className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600 dark:text-emerald-400" />
          </div>

          <DialogTitle className="text-center text-xl sm:text-2xl px-2">
            {!hasInventory && detectedCountry
              ? `No Inventories Available in ${detectedCountryName}`
              : "Select Your Country"}
          </DialogTitle>

          <DialogDescription className="text-center space-y-1 sm:space-y-2 px-2">
            {!hasInventory && detectedCountry ? (
              <>
                {/* <p className="text-sm sm:text-base">
                  Currently unavailable in <span className="font-bold text-black dark:text-white">{detectedCountryName}</span>.
                </p> */}
                <p className="text-sm sm:text-base">
                  Please select a country to continue.
                </p>
                {/* <p className="text-sm sm:text-base font-medium text-emerald-600 dark:text-emerald-400">
                  Please select a country from the available list to continue exploring.
                </p> */}
              </>
            ) : (
              <>
                <p className="text-sm sm:text-base">
                  Choose your country to see relevant billboard inventories and packages in your region.
                </p>
                <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Inventories are displayed based on your selected country</span>
                  <span className="sm:hidden">Based on selected country</span>
                </div>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 sm:pr-4 min-h-0">
          {countryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              <span className="ml-2 text-sm text-gray-600">Loading countries...</span>
            </div>
          ) : (
            <RadioGroup value={selectedCountry} onValueChange={setSelectedCountry}>
              <div className="space-y-1.5 sm:space-y-2 pb-2">
                {availableCountries.map((country: CountryData) => (
                  <div
                    key={country.id}
                    className={`
                      flex items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 rounded-lg border-2 transition-all cursor-pointer
                      ${selectedCountry === country.id
                        ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700'
                      }
                    `}
                    onClick={() => setSelectedCountry(country.id)}
                    data-testid={`country-option-${country.countryId}`}
                  >
                    <RadioGroupItem value={country.id} id={country.id} className="shrink-0" />
                    <Label
                      htmlFor={country.id}
                      className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-1"
                    >
                      <img
                        src={`https://flagcdn.com/w40/${getCountryFlagCode(country.name)}.png`}
                        alt={`${country.name} flag`}
                        className="w-7 h-5 sm:w-8 sm:h-6 object-cover rounded shadow-sm shrink-0"
                        onError={(e) => {
                          // Fallback to a generic flag if the specific flag fails to load
                          const target = e.target as HTMLImageElement;
                          target.src = `https://flagcdn.com/w40/un.png`;
                        }}
                      />
                      <div className="flex-1">
                        <span className="font-medium text-sm sm:text-base">{country.name}</span>
                        {country.iso && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {country.iso} • {country.currencyCode}
                          </div>
                        )}
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}
        </div>

        <div className="flex-shrink-0">
          <Button
            onClick={handleSelectCountry}
            disabled={!selectedCountry || countryLoading}
            className="w-full text-white bg-emerald-600 hover:bg-emerald-700 text-base h-11 font-medium disabled:opacity-50"
            data-testid="button-select-country"
          >
            {countryLoading
              ? "Loading..."
              : selectedCountry
                ? `Select ${availableCountries.find((c: CountryData) => c.id === selectedCountry)?.name || 'Country'}`
                : "Select Country"
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}